import React, { useCallback, useEffect, useState } from "react";
import { History, Undo2, Download } from "lucide-react";
import { toast } from "sonner";
import api, { inr, formatApiError } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

const when = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");
const COLS = "grid grid-cols-[150px_120px_minmax(0,1.4fr)_90px_120px_120px_150px] gap-3 px-4 items-center";

const getFile = async (b) => {
  try {
    const res = await api.get(`/ledger/import-batches/${b.batch_id}/file`, { responseType: "blob" });
    const u = window.URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = u; a.download = b.file_name || "upload.csv"; a.click();
    window.URL.revokeObjectURL(u);
  } catch { toast.error("Could not download the file"); }
};

const HistoryRow = ({ b, canUndo, onUndo }) => (
  <div data-testid={`upload-hist-row-${b.batch_id}`} className={`${COLS} py-2.5 text-sm`}>
    <span className="num text-xs">{when(b.created_at)}</span>
    <span className="text-xs break-words">{b.created_by_name || "—"}</span>
    <div className="min-w-0">
      <div className="font-medium break-words">{(b.accounts || []).join(", ") || b.label || "—"}</div>
      <div className="text-[11px] text-muted-foreground break-words">{b.scope === "bulk" ? "Bulk file" : "Account upload"}{b.label ? ` · ${b.label}` : ""}</div>
    </div>
    <span className="num text-xs">{b.debit_rows} due / {b.credit_rows} pay</span>
    <span className="num text-right">{inr(b.total_debit)}</span>
    <span className="num text-right text-[hsl(var(--positive))]">{inr(b.total_credit)}</span>
    <div className="justify-self-end flex items-center gap-1.5">
      {b.has_file ? <button data-testid={`upload-hist-file-${b.batch_id}`} onClick={() => getFile(b)} title={b.file_name} className="h-8 px-2 rounded-md border border-input text-xs flex items-center gap-1 hover:bg-muted transition-colors"><Download size={12} /> File</button> : <span className="text-[10px] text-muted-foreground">no copy</span>}
      {canUndo && <button data-testid={`upload-hist-undo-${b.batch_id}`} onClick={() => onUndo(b)} className="h-8 px-2 rounded-md border border-destructive/40 text-destructive text-xs flex items-center gap-1 hover:bg-destructive/10 transition-colors"><Undo2 size={12} /> Undo</button>}
    </div>
  </div>
);

export const UploadHistory = ({ canUndo, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const load = useCallback(() => api.get("/ledger/import-batches", { params: { limit: 500 } }).then((r) => setRows(r.data)).catch(() => setRows([])), []);
  useEffect(() => { if (open) load(); }, [open, load]);
  const undo = async (b) => {
    if (!window.confirm(`Undo this upload? ${b.debit_rows} due(s) and ${b.credit_rows} payment(s) will be removed from the statement.`)) return;
    try {
      const { data } = await api.delete(`/ledger/import-batches/${b.batch_id}`);
      toast.success(`Removed ${data.removed_dues} due(s), ${data.removed_payments} payment(s)`);
      load(); onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <>
      <button data-testid="upload-history-btn" onClick={() => setOpen(true)} className="h-10 px-4 rounded-md border border-input bg-card text-sm flex items-center gap-2 hover:bg-muted transition-colors"><History size={15} /> Upload history</button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="upload-history-dialog" className="bg-card max-w-5xl w-[calc(100%-1rem)] max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl">Ledger upload history</DialogTitle></DialogHeader>
          <div className="rounded-lg border border-border overflow-x-auto">
            <div className="min-w-[980px]">
              <div className={`${COLS} py-2.5 bg-muted text-xs font-semibold uppercase tracking-wide text-muted-foreground`}>
                <span>Date</span><span>Uploaded by</span><span>Account</span><span>Rows</span><span className="text-right">Dues</span><span className="text-right">Payments</span><span />
              </div>
              {rows.length === 0 ? <div data-testid="upload-history-empty" className="p-8 text-center text-sm text-muted-foreground">No uploads yet.</div>
                : <div className="divide-y divide-border">{rows.map((b) => <HistoryRow key={b.batch_id} b={b} canUndo={canUndo} onUndo={undo} />)}</div>}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
